// Ressources de traduction française. Structure imbriquée par "scope"
// pour éviter les collisions de clés. Convention :
//   common.*  : composants génériques (boutons, modales, statuts, dates)
//   sidebar.* : labels de la barre de navigation
//   auth.*    : login, logout, password
//   <page>.*  : textes spécifiques à une page (phones, jobs, accounts, etc.)
//
// Garder la PARITÉ de clés avec en.ts. Si tu ajoutes une clé ici, ajoute-la
// aussi dans en.ts. Le code TypeScript dérive le type des clés depuis fr.ts
// (cf. i18n/types.ts) pour avoir l'autocomplétion + erreur si une clé manque.

export const fr = {
  common: {
    save: "Enregistrer",
    cancel: "Annuler",
    delete: "Supprimer",
    edit: "Modifier",
    close: "Fermer",
    confirm: "Confirmer",
    yes: "Oui",
    no: "Non",
    loading: "Chargement…",
    error: "Erreur",
    success: "Succès",
    serverError: "Erreur serveur",
    notFound: "Introuvable",
    forbidden: "Accès refusé",
    unauthorized: "Non authentifié",
    none: "Aucun",
    all: "Tous",
    search: "Rechercher",
    filter: "Filtre",
    actions: "Actions",
    name: "Nom",
    status: "Statut",
    createdAt: "Créé",
    updatedAt: "Modifié",
    never: "jamais",
    refresh: "Rafraîchir",
    add: "Ajouter",
    new: "Nouveau",
    back: "Retour",
    retry: "Réessayer",
    skip: "Passer",
    abort: "Abandonner",
    stop: "Stop",
    pause: "Pause",
    resume: "Reprendre",
    start: "Démarrer",
    apply: "Appliquer",
    copy: "Copier",
    copied: "Copié",
    optional: "optionnel",
    required: "requis",
  },
  sidebar: {
    dashboard: "Tableau de bord",
    phones: "Téléphones",
    scripts: "Scripts",
    jobs: "Jobs",
    accounts: "Comptes IG",
    failures: "Failures",
    shareCenter: "Share Center",
    users: "Utilisateurs",
    documentation: "Documentation",
    logout: "Déconnexion",
  },
  auth: {
    login: {
      title: "Connexion",
      subtitle: "Accède au panel Velvet",
      email: "Email",
      password: "Mot de passe",
      submit: "Se connecter",
      submitting: "Connexion…",
      error: "Identifiants invalides",
    },
    logout: "Déconnexion",
    sessionExpired: "Ta session a expiré. Veuillez vous reconnecter.",
  },
  status: {
    online: "En ligne",
    offline: "Hors ligne",
    busy: "Occupé",
    error: "Erreur",
    active: "Actif",
    suspended: "Suspendu",
    deleted: "Supprimé",
    pending_validation: "À valider",
    pending: "En attente",
    dispatched: "Envoyé",
    running: "En cours",
    paused: "En pause",
    completed: "Terminé",
    cancelled: "Annulé",
    success: "Succès",
    failed: "Échec",
    debug: "debug",
    info: "info",
    warn: "warn",
  },
  dashboard: {
    title: "Tableau de bord",
    subtitle: "Vue d'ensemble de la ferme Velvet.",
    realtimeOn: "Realtime connecté",
    realtimeOff: "Realtime déconnecté",
    sectionPhones: "Téléphones",
    sectionAccounts: "Comptes Instagram",
    seeAllPhones: "Voir tous les téléphones →",
    seeAllAccounts: "Voir tous les comptes →",
    cards: {
      total: "Total",
      online: "En ligne",
      busy: "Occupé",
      offline: "Hors ligne",
      error: "Erreur",
      active: "Actifs",
      suspended: "Suspendus",
      deleted: "Supprimés",
      pendingReview: "À valider",
    },
  },
  phones: {
    title: "Téléphones",
    enrolledCount: "{{count}} tél. enrôlé",
    enrolledCount_plural: "{{count}} tél. enrôlés",
    addBtn: "Ajouter un téléphone",
    columns: {
      name: "Nom",
      status: "Statut",
      model: "Modèle",
      url: "URL agent",
      lastHeartbeat: "Dernier heartbeat",
    },
    actions: {
      stream: "Voir le stream",
      envVars: "Variables d'environnement",
      reboot: "Redémarrer",
      delete: "Supprimer",
      rename: "Renommer",
    },
    confirmDelete: "Supprimer ce téléphone ? Les jobs/runs/comptes liés seront aussi supprimés.",
    empty: "Aucun téléphone enrôlé. Suis le guide d'installation pour en ajouter un.",
    envModal: {
      title: "Variables d'environnement — {{name}}",
      description: "Variables exposées aux scripts via ${VAR}. Modifiables sans redéploiement.",
      keyPlaceholder: "NOM_VARIABLE",
      valuePlaceholder: "valeur",
      addRow: "Ajouter une variable",
      empty: "Aucune variable définie.",
    },
    addModal: {
      title: "Ajouter un téléphone",
      name: "Nom",
      connectionUrl: "URL de connexion (port 8889)",
      spoofedModel: "Modèle spoofé (optionnel)",
      submit: "Créer",
      enrollSshTitle: "Commande SSH d'enrôlement",
      enrollSshHint: "Copie et exécute ce one-liner sur le téléphone (à montrer une seule fois).",
    },
  },
  phoneStream: {
    title: "Stream {{name}}",
    connecting: "Connexion…",
    reconnecting: "Reconnexion (tentative {{n}}) dans {{s}}s",
    fullscreen: "Plein écran",
    reboot: "Redémarrer",
    ocr: "OCR",
    controls: {
      home: "Accueil",
      appSwitcher: "App switcher",
      lock: "Verrouiller",
      volumeUp: "Volume +",
      volumeDown: "Volume −",
    },
    notReachable: "Téléphone injoignable sur {{addr}} (timeout). Le serveur de stream H.264 est-il actif ?",
    instructions: "Clique-glisse sur le téléphone pour swipe, simple clic pour tap, clic long pour appui prolongé.",
  },
  scripts: {
    title: "Scripts",
    addBtn: "Nouveau script",
    importBtn: "Importer JSON",
    columns: {
      name: "Nom",
      type: "Type",
      lastUpdate: "Dernière modif",
      actions: "Actions",
    },
    actions: {
      edit: "Éditer",
      duplicate: "Dupliquer",
      export: "Exporter",
      delete: "Supprimer",
      launch: "Lancer un job",
    },
    confirmDelete: "Supprimer le script {{name}} ?",
    empty: "Aucun script. Crée-en un avec l'éditeur ou importe un JSON.",
    editor: {
      title: "Éditeur de script",
      saveBtn: "Enregistrer",
      saving: "Enregistrement…",
      validateBtn: "Valider la structure",
      runBtn: "Lancer un job",
      formatBtn: "Formater",
      nameLabel: "Nom",
      descriptionLabel: "Description",
      jobTypeLabel: "Type de job",
      contentLabel: "Contenu (JSONC)",
      isActiveLabel: "Actif",
      validationOk: "Structure valide",
      validationError: "Erreur de validation",
      unsaved: "Modifications non enregistrées",
      saved: "Enregistré",
    },
  },
  jobs: {
    title: "Jobs",
    countSuffix: "Auto-rafraîchissement toutes les 5 s.",
    countOne: "{{count}} job",
    countOther: "{{count}} jobs",
    newBtn: "Nouveau job",
    columns: {
      name: "Nom",
      script: "Script",
      phone: "Téléphone",
      mode: "Mode",
      progress: "Progression",
      status: "Statut",
      created: "Créé",
    },
    empty: "Aucun job pour ce filtre.",
    detail: {
      tabs: {
        runs: "Runs",
        logs: "Logs",
        script: "Script",
      },
      stats: {
        progress: "Progression",
        success: "Succès",
        failed: "Échecs",
        cancelled: "Annulés",
        pending: "En attente",
      },
      pausedBanner: {
        title: "Run en pause sur fail",
        manualTitle: "Run en pause manuelle",
        manualHint: "Le script est gelé sur le device. Prends la main via le stream, puis Continue pour reprendre là où il en était.",
        stepLabel: "Step",
        runLabel: "Run",
        pausedSince: "en pause depuis {{ago}}",
        autoAbort: "Auto-abort après 30 min si pas d'action.",
        retry: "Retry step",
        skip: "Passer",
        continue: "Continue",
        openStream: "Ouvrir le stream",
        abort: "Abandonner",
        confirmAbort: "Abandonner ce run ? Le run sera marqué failed.",
        screenshotHint: "Snapshot capturé au moment du fail. Utilise le stream pour résoudre manuellement avant Retry.",
      },
      runs: {
        empty: "Aucun run pour ce job.",
        columns: {
          id: "ID",
          status: "Statut",
          started: "Démarré",
          duration: "Durée",
          result: "Résultat / Erreur",
        },
      },
      logs: {
        empty: "Aucun log pour les runs de ce job (l'agent n'a peut-être encore rien envoyé).",
      },
      pause: "Mettre en pause",
      resume: "Reprendre",
      stop: "Arrêter",
      confirmStop: "Arrêter ce job ? Le run en cours sera annulé sur le téléphone.",
      live: "Live",
      offline: "Offline",
    },
    picker: {
      title: "Quel script lancer ?",
      noPhones: "Aucun téléphone disponible. Demande à un admin de t'assigner un téléphone.",
      noScripts: "Aucun script actif.",
    },
    launch: {
      title: "Lancer un job — {{script}}",
      jobName: "Nom du job",
      phone: "Téléphone",
      mode: "Mode",
      modeOneShot: "One-shot (1 run)",
      modeLoop: "Loop (plusieurs runs)",
      maxRunsLabel: "Limite",
      maxRunsLimited: "Limité",
      maxRunsInfinite: "Infini",
      maxRunsCount: "Nombre de runs",
      submit: "Lancer",
      submitting: "Lancement…",
      noPhones: "Aucun téléphone enrôlé.",
    },
  },
  accounts: {
    title: "Comptes Instagram",
    countSuffix: "{{n}} compte au total",
    countSuffix_plural: "{{n}} comptes au total",
    columns: {
      username: "Username",
      email: "Email",
      phone: "Téléphone IG",
      twoFa: "2FA",
      status: "Statut",
      createdAt: "Créé",
      device: "Tel. créateur",
      fullName: "Nom",
      followers: "Followers",
      posts: "Posts",
      lastSync: "Dernier sync",
      source: "Source",
    },
    actions: {
      copy: "Copier",
      exportTxt: "Exporter (txt)",
      delete: "Supprimer",
      add: "Ajouter un compte",
      view: "Voir les détails",
      syncNow: "Récupérer depuis Instagram",
      syncFailed: "Récupération échouée",
      retrySync: "Réessayer la synchro",
      edit: "Éditer",
      save: "Enregistrer",
      cancel: "Annuler",
      copyCreds: "Copier credentials",
      openProfile: "Ouvrir le profil",
    },
    confirmDelete: "Supprimer ce compte ? Les credentials seront perdus.",
    empty: "Aucun compte créé pour ce filtre.",
    filters: {
      status: "Statut",
      device: "Téléphone",
      search: "Rechercher (username, nom, email)",
      source: "Source",
      syncStatus: "Sync",
    },
    source: {
      agent: "Auto",
      manual: "Manuel",
    },
    sync: {
      never: "Jamais",
      ok: "OK",
      stale: "À rafraîchir",
      error: "Erreur",
      checkpoint: "Checkpoint",
      banned: "Banni",
    },
    stats: {
      total: "Total",
      active: "Actifs",
      suspended: "Suspendus",
      deleted: "Supprimés",
      pending: "À valider",
      postsToday: "Posts aujourd'hui",
      created: "Comptes créés",
      verification: "Vérification à faire",
    },
    pagination: {
      prev: "Précédent",
      next: "Suivant",
      page: "Page {{page}} / {{pages}}",
    },
    add: {
      title: "Ajouter un compte",
      username: "Username (requis)",
      igUserId: "IG user ID",
      fullName: "Nom complet",
      email: "Email",
      phoneNumber: "Numéro de téléphone",
      password: "Mot de passe",
      twoFaSecret: "Secret 2FA",
      proxy: "Proxy",
      notes: "Notes",
      submit: "Créer",
      submitting: "Création…",
      error: "Création échouée",
    },
    detail: {
      back: "Retour",
      followers: "Followers",
      following: "Following",
      posts: "Posts",
      overview: "Évolution",
      noMetrics: "Pas encore de métriques.",
      postsTitle: "Posts",
      noPosts: "Aucun post synchronisé.",
      credentials: "Credentials",
      password: "Mot de passe",
      twoFa: "Secret 2FA",
      notes: "Notes",
      notesPlaceholder: "Notes internes…",
      info: "Infos",
      igUserId: "IG user ID",
      device: "Tel. créateur",
      proxy: "Proxy",
      runId: "Run d'origine",
      createdAt: "Créé le",
      lastSync: "Dernier sync",
      reveal: "Voir",
      hide: "Cacher",
      likes: "likes",
      comments: "commentaires",
      views: "vues",
      syncQueued: "Synchronisation demandée",
      syncInProgress: "Récupération du profil Instagram en cours… (jusqu'à ~45 s)",
      syncErrorPrefix: "Échec de la récupération",
      notFound: "Compte introuvable",
      bioPlaceholder: "Pas de bio",
      creationIphone: "iPhone de création",
      statPosts: "Posts effectués",
      statViews: "Vues totales",
      statLikes: "Likes totaux",
      statWarmup: "Heures de warm-up",
      notesTitle: "Notes",
      lastPostTitle: "Dernier post",
      timeline: {
        title: "Frise chronologique",
        empty: "Aucun post ni session de warm-up pour le moment.",
        warmupLabel: "{{min}} min de warm-up",
        postLabel: {
          photo: "Photo publiée",
          video: "Vidéo publiée",
          carousel: "Carrousel publié",
          reel: "Reel publié",
        },
      },
      warmup: {
        addTitle: "Ajouter une session de warm-up",
        start: "Début",
        duration: "Durée (min)",
        note: "Note (optionnel)",
        add: "Ajouter",
        delete: "Supprimer la session",
        errStart: "Date de début invalide",
        errDuration: "Durée invalide (minutes > 0)",
        errAdd: "Ajout échoué",
        errDelete: "Suppression échouée",
      },
    },
    range: {
      d7: "7 j",
      d30: "30 j",
      d90: "90 j",
      all: "Tout",
    },
  },
  failures: {
    title: "Failures",
    subtitle: "Captures d'erreur des scripts (Phase 0 auto-rescue)",
    columns: {
      created: "Date",
      script: "Script",
      phone: "Tél.",
      step: "Step",
      error: "Erreur",
      screenshot: "Capture",
    },
    empty: "Aucune failure capturée.",
    askAi: "Demander à l'IA",
    asking: "Analyse…",
    apply: "Appliquer",
    ignore: "Ignorer",
    detail: {
      contextTab: "Contexte",
      ocrTab: "OCR",
      varsTab: "Variables runtime",
      rescueTab: "Tentatives IA",
      stepDef: "Définition du step",
      precedingSteps: "Steps précédents",
      errorMessage: "Message d'erreur",
      noScreenshot: "Pas de screenshot disponible.",
    },
  },
  users: {
    title: "Utilisateurs",
    countSuffix: "{{n}} compte",
    countSuffix_plural: "{{n}} comptes",
    addBtn: "Ajouter un utilisateur",
    columns: {
      user: "Utilisateur",
      role: "Rôle",
      phones: "Téléphones",
      status: "Statut",
      lastLogin: "Dernière connexion",
    },
    role: {
      admin: "Admin",
      employee: "Employé",
    },
    fullAccess: "accès complet",
    none: "aucun",
    create: {
      title: "Ajouter un utilisateur",
      email: "Email",
      name: "Nom",
      password: "Mot de passe initial (8 caractères min)",
      passwordHint: "L'employé pourra le changer après",
      role: "Rôle",
      roleEmployee: "Employé (accès limité aux tels assignés)",
      roleAdmin: "Admin (accès complet)",
      phones: "Téléphones assignés",
      noPhones: "Aucun téléphone enrôlé",
      submit: "Créer",
      submitting: "Création…",
    },
    assign: {
      title: "Téléphones de {{name}}",
      hint: "Coche les téléphones auxquels {{email}} a accès. La liste s'applique immédiatement (cache TTL ~60s).",
      saveCount: "Enregistrer ({{n}})",
    },
    resetPassword: {
      title: "Reset password — {{email}}",
      label: "Nouveau mot de passe (8 caractères min)",
      submit: "Reset",
      submitting: "Reset…",
      done: "✓ Mot de passe réinitialisé. Communique-le à {{name}} :",
      ok: "OK",
      tooShort: "Minimum 8 caractères",
    },
    confirmDelete: "Supprimer le compte {{email}} ? Cette action est irréversible.",
    selfRoleLocked: "Tu ne peux pas changer ton propre rôle",
    selfDeactivateLocked: "Tu ne peux pas désactiver ton propre compte",
    selfDeleteLocked: "Tu ne peux pas supprimer ton propre compte",
  },
  shareCenter: {
    title: "Share Center",
    subtitle: "Bibliothèque de médias et de légendes partagée avec les téléphones.",
    tabs: {
      media: "Médias",
      text: "Textes",
    },
    media: {
      uploadBtn: "Importer un média",
      uploading: "Import…",
      count: "{{n}} média",
      count_plural: "{{n}} médias",
      empty: "Aucun média. Importe une image ou une vidéo.",
      labelPlaceholder: "Label (optionnel)",
      tagsPlaceholder: "tags séparés par des virgules (optionnel)",
      confirmDelete: "Supprimer ce média ? L'original sera perdu.",
      preview: "Aperçu",
      downloadOriginal: "Original",
      downloadCleaned: "Nettoyé",
      cleaning: "Nettoyage…",
      downloadError: "Téléchargement échoué",
    },
    text: {
      addBtn: "Nouveau snippet",
      count: "{{n}} snippet",
      count_plural: "{{n}} snippets",
      empty: "Aucun snippet texte. Ajoute une légende promo.",
      titleLabel: "Titre",
      bodyLabel: "Texte",
      tagsLabel: "Tags (séparés par des virgules)",
      titlePlaceholder: "Ex : Légende promo printemps",
      bodyPlaceholder: "Le texte à copier-coller…",
      confirmDelete: "Supprimer ce snippet ?",
      copy: "Copier",
      copied: "Copié",
      save: "Enregistrer",
    },
  },
  documentation: {
    title: "Documentation",
    subtitle: "Commandes utiles et procédures de dépannage",
    sections: {
      daemon: "Daemon (velvetfingerd)",
      install: "Installation / mise à jour du tweak",
      troubleshooting: "Dépannage",
    },
    copyCmd: "Copier la commande",
    copied: "Copié",
  },
  language: {
    label: "Langue",
    fr: "Français",
    en: "English",
    detected: "Langue détectée automatiquement",
    saved: "Choix enregistré",
  },
};

// Type récursif : { foo: string | { ... } }. Évite le narrowing à des
// litéraux français impossibles à réassigner avec les valeurs anglaises.
export type FrResources = typeof fr;
